import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { api } from '../utils/api';
import { formatTimeLocale } from '../utils/i18nHelpers';
import ProtectedRoute from '../components/ProtectedRoute';
import Skeleton from '../components/Skeleton';

const COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const STATUS_COLORS = {
  confirmed: '#4ade80',
  pending: 'var(--orange)',
  cancelled: '#f87171',
  no_show: '#fdba74',
  completed: '#60a5fa',
};

export function UserAnalyticsPanel({ embedded = false }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar-EG' : 'en-GB';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getUserAnalytics().then(setData).catch(console.error).finally(() => setLoading(false));
  }, []);

  const tooltipStyle = { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-card)', color: 'var(--text-primary)', borderRadius: '8px' };

  const kpiCard = (label, value) => (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border-card)',
      borderRadius: '12px', padding: '1.2rem', textAlign: 'center',
    }}>
      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.3rem' }}>{label}</p>
      <p style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>{value}</p>
    </div>
  );

  if (loading) return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 1rem' }}>
      <Skeleton count={4} height="80px" />
    </div>
  );

  if (!data) return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 1rem' }}>
      <p style={{ color: 'var(--text-muted)' }}>{t('analytics.loadFailed')}</p>
    </div>
  );

  const statusData = Object.entries(data.booking_status_breakdown || {}).map(([name, value]) => ({ name, value }));

  return (
    <div style={{ maxWidth: '960px', margin: '0 auto', padding: '2rem 1rem' }}>
      {!embedded && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>{t('analytics.myAnalytics')}</h1>
          <Link to="/profile" style={{ color: 'var(--orange)', fontSize: '0.85rem', textDecoration: 'none' }}>{t('analytics.backToProfile')}</Link>
        </div>
      )}

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {kpiCard(t('analytics.totalBookings'), data.total_bookings)}
        {kpiCard(t('analytics.totalSpent'), `${data.total_spent.toLocaleString()} EGP`)}
        {kpiCard(t('analytics.memberSince'), data.member_since || '-')}
        {kpiCard(t('analytics.totalOrders'), data.total_orders)}
      </div>

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Spending trend */}
        {data.spending_by_month && data.spending_by_month.length > 0 && (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-card)', borderRadius: '12px', padding: '1rem' }}>
            <h2 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.8rem' }}>{t('analytics.spendingTrend')}</h2>
            <div style={{ height: '220px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.spending_by_month}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card)" />
                  <XAxis dataKey="month" stroke="var(--text-muted)" fontSize={10} />
                  <YAxis stroke="var(--text-muted)" fontSize={10} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Area type="monotone" dataKey="amount" stroke="var(--orange)" fill="var(--orange)" fillOpacity={0.15} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Bookings by month */}
        {data.bookings_by_month && data.bookings_by_month.length > 0 && (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-card)', borderRadius: '12px', padding: '1rem' }}>
            <h2 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.8rem' }}>{t('analytics.bookingsByMonth')}</h2>
            <div style={{ height: '220px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.bookings_by_month}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card)" />
                  <XAxis dataKey="month" stroke="var(--text-muted)" fontSize={10} />
                  <YAxis stroke="var(--text-muted)" fontSize={10} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="count" fill="var(--orange)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Booking status breakdown (Donut) */}
        {statusData.length > 0 && (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-card)', borderRadius: '12px', padding: '1rem' }}>
            <h2 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.8rem' }}>{t('analytics.statusBreakdown')}</h2>
            <div style={{ height: '220px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    innerRadius={50} outerRadius={80}
                    label={({ name, percent }) => `${t(`booking.status.${name}`, name)} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false} fontSize={10}>
                    {statusData.map((entry, i) => (
                      <Cell key={i} fill={STATUS_COLORS[entry.name] || COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Favorite services */}
        {data.favorite_services && data.favorite_services.length > 0 && (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-card)', borderRadius: '12px', padding: '1rem' }}>
            <h2 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.8rem' }}>{t('analytics.favoriteServices')}</h2>
            <div style={{ height: '220px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.favorite_services} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card)" />
                  <XAxis type="number" stroke="var(--text-muted)" fontSize={10} />
                  <YAxis dataKey="service_name" type="category" stroke="var(--text-muted)" fontSize={10} width={100} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Upcoming Bookings */}
      {data.upcoming_bookings && data.upcoming_bookings.length > 0 && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-card)', borderRadius: '12px', padding: '1rem', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.8rem' }}>{t('analytics.upcomingBookings')}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {data.upcoming_bookings.map(b => (
              <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem', borderRadius: '8px', background: 'var(--bg-base)' }}>
                <div>
                  <p style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)' }}>{b.service_name}</p>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{b.booking_date} at {formatTimeLocale(b.booking_time, locale)}</p>
                </div>
                <span style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem', borderRadius: '4px', background: 'rgba(34,197,94,0.1)', color: '#4ade80' }}>
                  {t(`booking.status.${b.status}`, b.status)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Bookings */}
      {data.recent_bookings && data.recent_bookings.length > 0 && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-card)', borderRadius: '12px', padding: '1rem', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.8rem' }}>{t('analytics.recentBookings')}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {data.recent_bookings.map(b => (
              <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem', borderRadius: '8px', background: 'var(--bg-base)' }}>
                <div>
                  <p style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)' }}>{b.service_name}</p>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{b.booking_date} - {b.total_price} EGP</p>
                </div>
                <span style={{
                  fontSize: '0.72rem', padding: '0.15rem 0.5rem', borderRadius: '4px',
                  color: STATUS_COLORS[b.status] || 'var(--text-muted)',
                  background: b.status === 'confirmed' ? 'rgba(34,197,94,0.1)' : b.status === 'cancelled' ? 'rgba(239,68,68,0.1)' : 'rgba(255,255,255,0.05)',
                }}>
                  {t(`booking.status.${b.status}`, b.status)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Favorites Lists */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
        {data.favorite_artists && data.favorite_artists.length > 0 && (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-card)', borderRadius: '12px', padding: '1rem' }}>
            <h2 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.8rem' }}>{t('analytics.favoriteArtists')}</h2>
            {data.favorite_artists.map((a, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderBottom: '1px solid var(--border-card)' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>{a.artist_name}</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{a.count} {t('analytics.bookingsLabel')}</span>
              </div>
            ))}
          </div>
        )}

        {data.favorite_branches && data.favorite_branches.length > 0 && (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-card)', borderRadius: '12px', padding: '1rem' }}>
            <h2 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.8rem' }}>{t('analytics.favoriteBranches')}</h2>
            {data.favorite_branches.map((b, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderBottom: '1px solid var(--border-card)' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>{b.branch_name}</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{b.total_spent.toLocaleString()} EGP</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function UserAnalytics() {
  return <ProtectedRoute><UserAnalyticsPanel /></ProtectedRoute>;
}
